import { useLazyQuery, useQuery } from "@apollo/client";
import { IonCol, IonGrid, IonRow } from "@ionic/react";
import { useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useHistory } from "react-router";
import { StringParam, useQueryParams } from "use-query-params";

import Button from "@components/buttons/Button";
import CircleButton from "@components/buttons/CircleButton";
import styles from "@components/explore/Explore.module.css";
import MarketplaceButton from "@components/explore/MarketplaceButton";
import HandleQuery from "@components/HandleQuery";
import Tag from "@components/labels/Tag";
import ExploreMarketplaceLoader from "@components/loaders/ExploreMarketplaceLoader";
import OverlayModal from "@components/OverlayModal";
import PinToggle from "@components/PinToggle";
import BodyCopy from "@components/typography/BodyCopy";
import ButtonLink from "@components/typography/ButtonLink";
import Heading1 from "@components/typography/Heading1";
import Heading2 from "@components/typography/Heading2";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import useMyClient from "@hooks/useMyClient";
import useMyExpert from "@hooks/useMyExpert";
import useNodesFromConnection from "@hooks/useNodesFromConnection";
import useOpener from "@hooks/useOpener";
import useSanitizeHtml from "@hooks/useSanitizeHtml";
import useScrollPosition from "@hooks/useScrollPosition";
import { ScrollPositionPageTypeEnum } from "@typing/Enums";
import {
  AppEventActionKindEnum,
  AppEventCreateDocument,
  AppEventCreateMutation,
  AppEventCreateMutationVariables,
  EmployerBenefitCategoryEnum,
  GetEmployerBenefitsDocument,
  GetEmployerBenefitsQuery,
  GetEmployerBenefitsQueryVariables,
  GetJourneyWithEmployerBenefitCategoriesDocument,
  GetJourneyWithEmployerBenefitCategoriesQuery,
  GetJourneyWithEmployerBenefitCategoriesQueryVariables,
  PinResourceTypeEnum
} from "@typing/Generated";
import { isValidHttpUrl, isValidPhoneNumber, joinListWithCommasAndAnd, shortenWebsite } from "@utils/stringUtils";

type BenefitEdge = NonNullable<
  NonNullable<NonNullable<GetEmployerBenefitsQuery["employerBenefits"]["edges"]>[number]>["node"]
>;

type Props = {
  journeyId: string;
};

type MarketplaceItemProps = {
  benefit: BenefitEdge;
  handleBenefitSelect: (benefit: BenefitEdge) => void;
  journeyId: string;
  selectedBenefitId: string | null | undefined;
};

const MarketplaceItem = ({ benefit, handleBenefitSelect, journeyId, selectedBenefitId }: MarketplaceItemProps) => {
  const sanitizedDescription = useSanitizeHtml(benefit.description, journeyId, benefit);
  const intl = useIntl();
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // this logic handles tabbing into anchor tags in truncated text
  const renderKey = isFocused ? "focused" : "not-focused";

  const handleFocus = () => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    setIsFocused(true);
  };

  const handleBlur = () => {
    blurTimeoutRef.current = setTimeout(() => {
      setIsFocused(false);
    }, 100);
  };
  return (
    <ButtonLink data-testid="employer-benefit" onClick={() => handleBenefitSelect(benefit)} stylized={false}>
      <IonGrid
        className={`${styles.benefitContainer} ion-no-padding ${
          selectedBenefitId === benefit.id ? styles.selectedBenefit : ""
        }`}
        key={benefit.id}
      >
        <IonRow>
          <IonCol className={styles.topSpacing} sizeLg="auto" sizeMd="auto" sizeSm="12" sizeXl="auto" sizeXs="12">
            {benefit.photoUrl && <img className={styles.benefitImage} src={benefit.photoUrl} />}
          </IonCol>
          <IonCol>
            <div className={styles.benefitHeader}>
              <Heading2 color="black">{benefit.vendorName}</Heading2>

              <div className={`${styles.tags} ion-hide-lg-down`}>
                {benefit.categories?.map(category => (
                  <Tag
                    color="gray-50"
                    key={category}
                    text={
                      category === EmployerBenefitCategoryEnum.OTHER && benefit.categoryOther
                        ? benefit.categoryOther
                        : intl.formatMessage({ id: `models.employerBenefitCategory.${category}` })
                    }
                  />
                ))}
              </div>
            </div>

            <div>
              {benefit.description && (
                <div className={isFocused ? "" : styles.truncate} key={renderKey}>
                  <BodyCopy color="gray" onBlur={handleBlur} onFocus={handleFocus}>
                    {sanitizedDescription}
                  </BodyCopy>
                </div>
              )}
            </div>
            <div className={`${styles.tags} ion-hide-md-up`}>
              {benefit.categories?.map(category => (
                <Tag
                  color="gray-50"
                  key={category}
                  text={
                    category === EmployerBenefitCategoryEnum.OTHER && benefit.categoryOther
                      ? benefit.categoryOther
                      : intl.formatMessage({ id: `models.employerBenefitCategory.${category}` })
                  }
                />
              ))}
            </div>
          </IonCol>
        </IonRow>
      </IonGrid>
    </ButtonLink>
  );
};

const Marketplace = ({ journeyId }: Props) => {
  const myExpert = useMyExpert();
  const myClient = useMyClient();
  const history = useHistory();
  const intl = useIntl();

  const getJourney = useQuery<
    GetJourneyWithEmployerBenefitCategoriesQuery,
    GetJourneyWithEmployerBenefitCategoriesQueryVariables
  >(GetJourneyWithEmployerBenefitCategoriesDocument, {
    variables: {
      id: journeyId
    }
  });

  const [getEmployerBenefits, employerBenefitsQuery] = useLazyQuery<
    GetEmployerBenefitsQuery,
    GetEmployerBenefitsQueryVariables
  >(GetEmployerBenefitsDocument);

  useEffect(() => {
    if (getJourney.data?.journey && getJourney?.data.journey.owner.employer?.hideBenefits && !myExpert) {
      history.push(`/client/journeys/${journeyId}/care-plan`);
    }
  }, [getJourney.data?.journey, history, journeyId, myExpert]);

  useEffect(() => {
    if (myExpert || !getJourney.data?.journey.owner.employer?.hideBenefits) {
      getEmployerBenefits({
        variables: {
          count: 30,
          journeyId
        }
      });
    }
  }, [getEmployerBenefits, journeyId, myExpert, getJourney.data?.journey.owner.employer?.hideBenefits]);

  useEffect(() => {
    if (
      journeyId &&
      !employerBenefitsQuery.loading &&
      employerBenefitsQuery.data?.employerBenefits.pageInfo.hasNextPage
    )
      employerBenefitsQuery
        .fetchMore({
          variables: {
            journeyId
          }
        })
        .then(res => {
          const edges = res.data.employerBenefits?.edges;
          const result: BenefitEdge[] = [];

          if (edges) {
            edges.forEach(edge => {
              if (edge?.node) {
                result.push(edge.node);
              }
            });
          }
        });
  }, [employerBenefitsQuery, journeyId]);

  const [query] = useQueryParams({
    benefitId: { ...StringParam, urlName: "benefitId" }
  });

  useScrollPosition(ScrollPositionPageTypeEnum.MARKETPLACE, !employerBenefitsQuery.loading);

  const allBenefits = useNodesFromConnection(employerBenefitsQuery.data?.employerBenefits);

  const [selectedBenefitId, setSelectedBenefitId] = useState<string | null | undefined>(query.benefitId);

  const selectedBenefit = allBenefits.find(benefit => benefit.id === selectedBenefitId) ?? null;

  const benefitOpener = useOpener(!!selectedBenefitId);

  const selectedBenefitSanitizedDescription = useSanitizeHtml(selectedBenefit?.description, journeyId, selectedBenefit);

  const [createAppEvent] = useMutationWithValidation<AppEventCreateMutation, AppEventCreateMutationVariables>(
    AppEventCreateDocument,
    "appEvent"
  );

  const handleBenefitSelect = (benefit: BenefitEdge) => {
    if (selectedBenefitId === benefit.id) {
      setSelectedBenefitId(null);
      benefitOpener.close();
    } else {
      setSelectedBenefitId(benefit.id);
      benefitOpener.open();
      createAppEvent({
        actionKind: AppEventActionKindEnum.EMPLOYER_BENEFIT_OPENED,
        journeyId,
        resourceId: benefit.id
      });
    }
  };

  const handleClickThrough = (benefitId: string, type: string) => {
    if (myClient) {
      createAppEvent({
        actionKind: AppEventActionKindEnum.EMPLOYER_BENEFIT_CLICK_THROUGH,
        context: type,
        journeyId,
        resourceId: benefitId
      });
    }
  };

  return (
    <div>
      <HandleQuery loadingContent={<ExploreMarketplaceLoader />} query={employerBenefitsQuery}>
        {_data => (
          <div className={styles.marketplaceItems} key="marketplaceItems">
            {allBenefits.map(benefit => (
              <MarketplaceItem
                benefit={benefit}
                handleBenefitSelect={handleBenefitSelect}
                journeyId={journeyId}
                key={benefit.id}
                selectedBenefitId={selectedBenefitId}
              />
            ))}
          </div>
        )}
      </HandleQuery>
      <OverlayModal onIonModalWillDismiss={() => setSelectedBenefitId(null)} opener={benefitOpener}>
        <div>
          {selectedBenefit && (
            <>
              <div className={styles.modalHeader}>
                <CircleButton
                  ariaLabelKey="accessibility.ariaLabels.close"
                  color="gray-300"
                  fill="clear"
                  icon="xmark"
                  onClick={benefitOpener.close}
                  size="medium"
                />
              </div>
              {selectedBenefit.photoUrl && (
                <div className="align-center">
                  <img className={styles.selectedBenefitImage} src={selectedBenefit.photoUrl} />
                </div>
              )}
              <div className={styles.content}>
                <IonGrid className={`ion-no-padding ion-align-items-center ${styles.selectedGrid}`}>
                  <IonRow>
                    <IonCol>
                      <Heading1>{selectedBenefit.vendorName}</Heading1>
                    </IonCol>
                    <IonCol size="auto">
                      <PinToggle
                        color="gray-150"
                        journeyId={journeyId}
                        resourceId={selectedBenefit.id}
                        resourceType={PinResourceTypeEnum.EMPLOYERBENEFIT}
                      />
                    </IonCol>
                  </IonRow>
                  <IonRow>
                    <IonCol>
                      <BodyCopy bold color="primary-300">
                        {joinListWithCommasAndAnd(
                          selectedBenefit.categories.map(category => {
                            if (category === EmployerBenefitCategoryEnum.OTHER && selectedBenefit.categoryOther) {
                              return selectedBenefit.categoryOther;
                            }

                            return intl.formatMessage({ id: `models.employerBenefitCategory.${category}` });
                          }),
                          intl
                        )}
                      </BodyCopy>
                    </IonCol>
                  </IonRow>
                  {selectedBenefit.emailLabel && selectedBenefit.emailLink && (
                    <IonRow>
                      <IonCol>
                        <MarketplaceButton
                          href={
                            isValidHttpUrl(selectedBenefit.emailLink)
                              ? selectedBenefit.emailLink
                              : `mailto:${selectedBenefit.emailLink}`
                          }
                          icon="at"
                          onClick={() => handleClickThrough(selectedBenefit.id, "email")}
                          text={
                            isValidHttpUrl(selectedBenefit.emailLabel)
                              ? shortenWebsite(selectedBenefit.emailLabel)
                              : selectedBenefit.emailLabel
                          }
                        />
                      </IonCol>
                    </IonRow>
                  )}
                  {selectedBenefit.links.length > 0 && (
                    <IonRow>
                      <IonCol>
                        {selectedBenefit.links.map((link, index) => (
                          <div className={index !== 0 ? styles.linkSpacing : ""} key={link}>
                            <MarketplaceButton
                              href={link}
                              icon={index === 0 ? "globe" : undefined}
                              onClick={() => handleClickThrough(selectedBenefit.id, "website")}
                              text={shortenWebsite(link)}
                            />
                          </div>
                        ))}
                      </IonCol>
                    </IonRow>
                  )}
                  {selectedBenefit.phoneNumberLabel && selectedBenefit.phoneNumberLink && (
                    <IonRow>
                      <IonCol>
                        <MarketplaceButton
                          href={
                            isValidPhoneNumber(selectedBenefit.phoneNumberLink)
                              ? `tel:${selectedBenefit.phoneNumberLink}`
                              : selectedBenefit.phoneNumberLink
                          }
                          icon="phone"
                          onClick={() => handleClickThrough(selectedBenefit.id, "phone")}
                          text={selectedBenefit.phoneNumberLabel}
                        />
                      </IonCol>
                    </IonRow>
                  )}
                </IonGrid>
                <div>
                  <BodyCopy color="gray">{selectedBenefitSanitizedDescription}</BodyCopy>
                </div>
                {selectedBenefit.links.length > 0 && (
                  <div className={styles.selectedBenefitButton}>
                    <Button href={selectedBenefit.links[0]} rel="noopener noreferrer" target="_blank">
                      <FormattedMessage id="pages.client.marketplace.goToSite" />
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </OverlayModal>
    </div>
  );
};

export default Marketplace;
